import { createStore } from 'vuex';
import axios from '../config/axios';
import auth from './modules/auth';

const suscripcionesModule = {
  namespaced: true,
  state: {
    suscripciones: []
  },
  mutations: {
    setSuscripciones(state, suscripciones) {
      state.suscripciones = suscripciones;
    },
    clearSuscripciones(state) {
      state.suscripciones = [];
    }
  },
  actions: {
    async fetchSuscripciones({ commit }, mostrarTodas = false) {
      try {
        console.log('Obteniendo suscripciones...');
        const { data } = await axios.get('/api/suscripciones', {
          params: { todas: mostrarTodas }
        });
        console.log('Suscripciones recibidas:', data);
        commit('setSuscripciones', data);
        return data;
      } catch (error) {
        console.error('Error al cargar suscripciones:', error);
        throw error;
      }
    }
  },
  getters: {
    getSuscripciones: state => state.suscripciones
  }
};

export default createStore({
  state: {
    gastos: [],
    categorias: [],
    lastFetch: null,
    isFetching: false,
    gastosPendientesCompartidos: [],
    invitacionesGrupoPendientes: 0,
    isFetchingNotificaciones: false
  },
  
  mutations: {
    setGastos(state, data) {
      if (Array.isArray(data)) {
        state.gastos = data;
      } else if (data && Array.isArray(data.gastos)) {
        state.gastos = data.gastos;
      } else {
        state.gastos = [];
      }
    },
    
    addGasto(state, gasto) {
      // Añadir el gasto al principio del array para que aparezca primero en la lista
      state.gastos.unshift(gasto);
    },
    
    removeGasto(state, id) {
      state.gastos = state.gastos.filter(g => g.id !== id);
    },
    
    setCategorias(state, categorias) {
      state.categorias = categorias;
    },
    
    setLastFetch(state, timestamp) {
      state.lastFetch = timestamp;
    },
    
    setIsFetching(state, status) {
      state.isFetching = status;
    },

    clearData(state) {
      state.gastos = [];
      state.categorias = [];
      state.lastFetch = null;
      state.isFetching = false;
      state.gastosPendientesCompartidos = [];
      state.invitacionesGrupoPendientes = 0;
      state.isFetchingNotificaciones = false;
    },

    setNotificacionesGrupo(state, data) {
      state.gastosPendientesCompartidos = (data && data.gastos_compartidos) || [];
      state.invitacionesGrupoPendientes = ((data && data.invitaciones) || []).length;
    },

    removeGastoPendienteCompartido(state, id) {
      const nid = parseInt(id, 10);
      state.gastosPendientesCompartidos = state.gastosPendientesCompartidos.filter(
        (g) => parseInt(g.id, 10) !== nid
      );
    },

    setIsFetchingNotificaciones(state, status) {
      state.isFetchingNotificaciones = status;
    }
  },
  
  actions: {
    async fetchGastos({ commit, state }) {
      if (state.isFetching) {
        console.log('Ya hay una petición en curso, esperando...');
        return state.gastos;
      }
      
      try {
        commit('setIsFetching', true);
        console.log('Obteniendo todos los gastos...');
        const { data } = await axios.get('/api/gastos/todos');
        console.log('Total de gastos recibidos:', Array.isArray(data) ? data.length : 'N/A');
        
        if (Array.isArray(data)) {
          commit('setGastos', data);
        } else if (data && Array.isArray(data.gastos)) {
          commit('setGastos', data.gastos);
        } else {
          console.warn('Formato de datos inesperado:', data);
          commit('setGastos', []);
        }
        
        commit('setLastFetch', Date.now());
        return data;
      } catch (error) {
        console.error('Error al cargar gastos:', error);
        throw error;
      } finally {
        commit('setIsFetching', false);
      }
    },
    
    async fetchUltimosGastos({ commit, state, dispatch }, force = false) {
      const ahora = Date.now();
      const tiempoCache = 60 * 1000;
      
      if (!force && state.lastFetch && (ahora - state.lastFetch < tiempoCache)) {
        console.log('Usando datos en caché, última actualización hace', 
          Math.round((ahora - state.lastFetch) / 1000), 'segundos');
        return state.gastos;
      }
      
      if (state.isFetching) {
        console.log('Ya hay una petición en curso, esperando...');
        return state.gastos;
      }
      
      try {
        commit('setIsFetching', true);
        console.log('Obteniendo últimos gastos...');
        
        // Obtener todos los gastos para permitir paginación en el cliente
        const { data } = await axios.get('/api/gastos/todos');
        
        console.log('Gastos recibidos:', 
          Array.isArray(data) ? data.length : 
          (data && Array.isArray(data.gastos) ? data.gastos.length : 'N/A'));
        
        if (Array.isArray(data)) {
          commit('setGastos', data);
        } else if (data && Array.isArray(data.gastos)) {
          commit('setGastos', data.gastos);
        } else {
          console.warn('Formato de datos inesperado:', data);
          commit('setGastos', []);
        }
        
        commit('setLastFetch', Date.now());
        return data;
      } catch (error) {
        console.error('Error al cargar últimos gastos:', error);
        throw error;
      } finally {
        commit('setIsFetching', false);
      }
    },
    
    async fetchCategorias({ commit, state }) {
      if (state.categorias && state.categorias.length > 0) {
        console.log('Usando categorías en caché');
        return state.categorias;
      }
      
      try {
        console.log('Obteniendo categorías...');
        const { data } = await axios.get('/api/categorias');
        console.log('Categorías recibidas:', data.length);
        commit('setCategorias', data);
        return data;
      } catch (error) {
        console.error('Error al cargar categorías:', error);
        throw error;
      }
    },
    
    async initializeData({ dispatch }) {
      try {
        console.log('Inicializando datos de la aplicación...');
        await Promise.all([
          dispatch('fetchUltimosGastos'),
          dispatch('fetchCategorias'),
          dispatch('suscripciones/fetchSuscripciones', false, { root: true }),
          dispatch('fetchNotificacionesGrupo').catch(() => null)
        ]);
        console.log('Datos inicializados correctamente');
      } catch (error) {
        console.error('Error al inicializar datos:', error);
        throw error;
      }
    },

    async fetchNotificacionesGrupo({ commit, state }) {
      if (state.isFetchingNotificaciones) {
        return;
      }
      try {
        commit('setIsFetchingNotificaciones', true);
        const { data } = await axios.get('/api/grupos/notificaciones');
        commit('setNotificacionesGrupo', data || {});
        return data;
      } catch (error) {
        commit('setNotificacionesGrupo', { gastos_compartidos: [], invitaciones: [] });
        throw error;
      } finally {
        commit('setIsFetchingNotificaciones', false);
      }
    },
    
    async addGasto({ commit }, gastoData) {
      try {
        console.log('Añadiendo gasto al estado:', gastoData);
        
        // Verificar que el gasto tenga un ID
        if (!gastoData || !gastoData.id) {
          console.warn('No se puede añadir un gasto sin ID al estado');
          return;
        }
        
        // Crear un objeto gasto completo con los datos recibidos
        // Asegurarse de que todos los campos necesarios estén presentes
        const gasto = {
          id: gastoData.id,
          usuario_id: gastoData.usuario_id || null,
          fecha: gastoData.fecha || new Date().toISOString().split('T')[0],
          concepto: gastoData.concepto || '',
          cantidad: gastoData.cantidad || 0,
          cantidad_total: gastoData.cantidad_total || 0,
          tipo_division: gastoData.tipo_division || 'total',
          categoria_id: gastoData.categoria_id || null,
          categoria_nombre: gastoData.categoria_nombre || ''
        };
        
        console.log('Gasto preparado para añadir al estado:', gasto);
        
        // Añadir el gasto al estado
        commit('addGasto', gasto);
        
        return gasto;
      } catch (error) {
        console.error('Error al añadir gasto al estado:', error);
        throw error;
      }
    },
    
    async removeGasto({ commit }, id) {
      try {
        console.log('Eliminando gasto del estado con ID:', id);
        
        // Verificar que el ID sea válido
        if (!id) {
          console.warn('No se puede eliminar un gasto sin ID del estado');
          return;
        }
        
        // Eliminar el gasto del estado
        commit('removeGasto', id);
        
        return id;
      } catch (error) {
        console.error('Error al eliminar gasto del estado:', error);
        throw error;
      }
    }
  },
  
  getters: {
    getGastos: state => state.gastos,
    getCategorias: state => state.categorias,
    gastosPendientesCompartidos: state => state.gastosPendientesCompartidos || [],
    gastosPendientesCount: state => (state.gastosPendientesCompartidos || []).length,
    invitacionesGrupoCount: state => state.invitacionesGrupoPendientes || 0
  },
  
  modules: {
    auth,
    suscripciones: suscripcionesModule
  }
}); 